import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Modal, Form, Badge, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';
import DataTable from '../components/common/DataTable';
import PaginationSection from '../components/transactions/PaginationSection';
import usePayees from '../hooks/usePayees';

const Payees = () => {
  const { activeProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  const { payees, pagination, loading, error, fetchPayees, addPayee, updatePayee } = usePayees();

  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    page: 1,
    limit: 50
  });

  const [showModal, setShowModal] = useState(false);
  const [editingPayee, setEditingPayee] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    type: 'Vendor', 
    isActive: true,
    email: '',
    phone: '',
    taxId: '',
    address: '',
    notes: ''
  });
  const [formError, setFormError] = useState(null);

  const isEditor = user?.type === 'admin' || 
                   activeProject?.members?.find(m => m.user === user?._id)?.role === 'editor' ||
                   activeProject?.members?.find(m => m.user === user?._id)?.role === 'owner';

  const loadPayees = useCallback(() => {
    if (activeProject?._id) {
      fetchPayees(activeProject._id, filters);
    }
  }, [activeProject?._id, fetchPayees, filters]);

  useEffect(() => {
    loadPayees();
  }, [loadPayees]);

  if (!activeProject) return <div className="p-5 text-center">Please select a project.</div>;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleOpenModal = (payee = null) => {
    setFormError(null);
    if (payee) {
      setEditingPayee(payee);
      setFormData({ 
        name: payee.name, 
        type: payee.type, 
        isActive: payee.isActive,
        email: payee.email || '',
        phone: payee.phone || '',
        taxId: payee.taxId || '',
        address: payee.address || '',
        notes: payee.notes || ''
      });
    } else {
      setEditingPayee(null);
      setFormData({ 
        name: '', 
        type: 'Vendor', 
        isActive: true,
        email: '',
        phone: '',
        taxId: '',
        address: '',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!formData.name.trim()) return setFormError('Name is required');

    const { name, type, isActive, ...metadata } = formData;
    let res;
    if (editingPayee) {
      res = await updatePayee(activeProject._id, editingPayee._id, name, type, isActive, metadata);
    } else {
      res = await addPayee(activeProject._id, name, type, isActive, metadata);
    }

    if (res.success) {
      setShowModal(false);
      loadPayees(); // Refresh list to show correctly with current filters/sorting
    } else {
      setFormError(res.error);
    }
  };

  const tableHeaders = [
    { label: 'Name' },
    { label: 'Type' },
    { label: 'Contact Info' },
    { label: 'Status' },
    ...(isEditor ? [{ label: 'Actions', align: 'right' }] : [])
  ];

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Payees Center</h2>
        <div className="text-muted small">Total: {pagination.total} records</div>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <Row className="align-items-center">
            <Col md={3} className="mb-2 mb-md-0">
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text className="bg-white border-right-0">
                    <i className="fas fa-search text-muted"></i>
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  name="search"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="border-left-0"
                />
              </InputGroup>
            </Col>
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Control
                as="select"
                size="sm"
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="Vendor">Vendor</option>
                <option value="Customer">Customer</option>
                <option value="Employee">Employee</option>
                <option value="Other">Other</option>
              </Form.Control>
            </Col>
            <Col md={2} className="mb-2 mb-md-0">
              <Form.Control
                as="select"
                size="sm"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Control>
            </Col>
            <Col md={2} className="mb-2 mb-md-0">
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text className="bg-light border-right-0 small">Rows</InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  as="select"
                  name="limit"
                  value={filters.limit}
                  onChange={handleFilterChange}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Form.Control>
              </InputGroup>
            </Col>
            <Col md={3} className="text-md-right">
              {isEditor && (
                <Button variant="primary" size="sm" onClick={() => handleOpenModal()}>
                  <i className="fas fa-plus mr-1"></i> Add Payee
                </Button>
              )}
            </Col>
          </Row>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="card shadow-sm border-0">
        <DataTable 
          headers={tableHeaders}
          data={payees} 
          loading={loading}
          emptyMessage="No payees found matching your criteria."
          renderRow={(payee) => (
            <tr key={payee._id}>
              <td className="align-middle font-weight-bold">{payee.name}</td>
              <td className="align-middle">
                <Badge variant={
                  payee.type === 'Vendor' ? 'secondary' : 
                  payee.type === 'Customer' ? 'info' : 
                  payee.type === 'Employee' ? 'warning' : 'light'
                }>
                  {payee.type}
                </Badge>
              </td>
              <td className="align-middle small">
                {payee.email && (
                  <div className="mb-1 text-truncate" style={{ maxWidth: '200px' }}>
                    <i className="fas fa-envelope mr-1 text-muted"></i>
                    {payee.email}
                  </div>
                )}
                {payee.phone && (
                  <div className="text-truncate">
                    <i className="fas fa-phone mr-1 text-muted"></i>
                    {payee.phone}
                  </div>
                )}
                {!payee.email && !payee.phone && (
                  <span className="text-muted italic small">No contact info</span>
                )}
              </td>
              <td className="align-middle">
                {payee.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="danger">Inactive</Badge>}
              </td>
              {isEditor && (
                <td className="text-right align-middle">
                  <Button variant="link" size="sm" onClick={() => handleOpenModal(payee)}>
                    <i className="fas fa-edit"></i> Edit
                  </Button>
                </td>
              )}
            </tr>
          )}
        />
        <div className="px-3 pb-3">
          <PaginationSection 
            page={pagination.currentPage} 
            pages={pagination.pages} 
            setPage={handlePageChange} 
          />
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingPayee ? 'Edit Payee' : 'Add Payee'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Display Name</Form.Label>
                        <Form.Control 
                            type="text" size="sm"
                            value={formData.name} 
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            required 
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Payee Type</Form.Label>
                        <Form.Control 
                            as="select" size="sm"
                            value={formData.type} 
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                        >
                            <option value="Vendor">Vendor</option>
                            <option value="Customer">Customer</option>
                            <option value="Employee">Employee</option>
                            <option value="Other">Other</option>
                        </Form.Control>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Email Address</Form.Label>
                        <Form.Control 
                            type="email" size="sm"
                            value={formData.email} 
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            placeholder="vendor@example.com"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Phone Number</Form.Label>
                        <Form.Control 
                            type="text" size="sm"
                            value={formData.phone} 
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="(555) 000-0000"
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Tax ID / SSN / EIN</Form.Label>
                        <Form.Control 
                            type="text" size="sm"
                            value={formData.taxId} 
                            onChange={e => setFormData({ ...formData, taxId: e.target.value })}
                            placeholder="For 1099 tracking"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label className="small font-weight-bold">Full Address</Form.Label>
                        <Form.Control 
                            as="textarea" rows={1} size="sm"
                            value={formData.address} 
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                            placeholder="123 Main St, City, State, Zip"
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label className="small font-weight-bold">Internal Notes</Form.Label>
                <Form.Control 
                    as="textarea" rows={2} size="sm"
                    value={formData.notes} 
                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Payment terms, special instructions, etc."
                />
            </Form.Group>

            <Form.Group className="mb-0 mt-4 d-flex align-items-center">
                <Form.Check 
                    type="switch"
                    id="isActive-switch"
                    label="Active"
                    checked={formData.isActive}
                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="ml-3 small text-muted">Inactive payees are hidden from transaction dropdowns.</span>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit" className="px-4 font-weight-bold">Save Payee</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Payees;
